// Top-level providers. Per §6.2 a CSRF bootstrap call would happen here;
// stubbed in Phase 0 because A1 owns the auth feature and ships /auth/csrf wiring.
// NOTE (P0-62 R94): PresenceFab was removed from AppShell — RobotPage now renders
// TradersOnlineBadge inline (R88). The global FAB caused a duplicate "0 TRADERS
// ONLINE" at the bottom of RobotPage because it used a different query key and
// rendered before its query resolved.
import { Suspense, useEffect, type ReactNode } from "react";
import { QueryClientProvider } from "@/lib/query";
import { Outlet, useLocation } from "react-router-dom";
import { queryClient } from "@/lib/query/client";
import { ThemeProvider } from "@/design/theme-provider";
import { SurfaceModeProvider } from "@/features/layout/surface-mode-provider";
import { Toaster } from "@/components/ui/toast";
import { AppErrorBoundary, useNetworkStatus } from "@/components/AppResilience";
import { configureVersionSkewReloadGuard } from "@/lib/version-skew";
import { isTradingActivityInProgress } from "@/features/robot/trading-activity-reload-guard";
import { InteractionGuard } from "@/components/InteractionGuard";
import { SuspenseFallbackTimeout } from "@/components/SuspenseFallbackTimeout";
import { ScrollRestorationToTop } from "@/components/ScrollRestorationToTop";
import { PushSubscriptionButton } from "@/pwa/PushSubscriptionButton";
import {
  fetchTradersOnlineShared,
  tradersOnlineKey,
} from "@/lib/hooks/useTradersOnline";
import {
  badgeClass,
  type StatusKey,
} from "@/lib/catalog-pending/visual-a11y/statusBadgeMap";
import { isStaticGamificationShowcasePath } from "@/features/gamification/showcase/static-showcase-path";

const CATALOG_STATUS_BADGES: ReadonlyArray<{
  readonly id: string;
  readonly key: StatusKey;
}> = [
  { id: "webhook-processed", key: "matrix-webhook-processed" },
  { id: "webhook-failed", key: "matrix-webhook-failed" },
  { id: "webhook-ignored", key: "matrix-webhook-ignored" },
  { id: "webhook-pending", key: "matrix-webhook-pending" },
  { id: "wallet-win", key: "wallet-win" },
  { id: "wallet-loss", key: "wallet-loss" },
  { id: "wallet-pending", key: "wallet-pending" },
  { id: "subscription-active", key: "subscription-active" },
  { id: "subscription-grace", key: "subscription-grace" },
  { id: "subscription-pending", key: "subscription-pending" },
  { id: "subscription-expired", key: "subscription-expired" },
  { id: "subscription-canceled", key: "subscription-canceled" },
  { id: "tokens-consume", key: "tokens-consume" },
];

// R16 version-skew (#deploy-stale-tab): the SPA only auto-reloads onto a new
// build when the operator is NOT trading. Wired at module scope so the guard
// is in place before the first liveness probe fires (useNetworkStatus below).
configureVersionSkewReloadGuard(isTradingActivityInProgress);

export function AppShell({
  children,
}: {
  children?: ReactNode;
}): React.JSX.Element {
  const location = useLocation();
  const staticShowcase = isStaticGamificationShowcasePath(location.pathname);
  // P0-115 R259: SuspenseFallbackTimeout swaps the bare SplashLoader for a CTA
  // overlay after 10s, fixing the "tela preta que não sobe" case where a lazy
  // chunk never resolves (CDN flake, stale service-worker manifest). Specific
  // pages (RobotPage) wrap themselves in BlankScreenWatchdog to also catch
  // hangs that happen *after* the chunk loads (e.g. RequireAuth session fetch
  // stuck on isLoading=true).
  useEffect(() => {
    if (staticShowcase) return;
    void queryClient
      .fetchQuery({
        queryKey: tradersOnlineKey,
        queryFn: fetchTradersOnlineShared,
        staleTime: 30_000,
      })
      .catch(() => undefined);
  }, [staticShowcase]);

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <SurfaceModeProvider>
            <InteractionGuard />
            {staticShowcase ? null : <ConnectedRuntimeProbe />}
            {/* Issue #176-2: snap window.scrollY to 0 on every push/replace
              before the destination route paints. Mounted here so every
              child route (login → /, /robot, /comprar) inherits the
              guarantee. POP is left to the dashboard tree's
              LayoutScrollRestoration. */}
            <ScrollRestorationToTop />
            <CatalogA11ySurface />
            <Suspense fallback={<RouterRootSuspenseFallback />}>
              {children ?? <Outlet />}
            </Suspense>
            {staticShowcase ? null : <PushSubscriptionButton />}
            <Toaster richColors closeButton />
          </SurfaceModeProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

/** Connected app probes are omitted only for the public static Pages demo. */
function ConnectedRuntimeProbe(): null {
  useNetworkStatus();
  return null;
}

/**
 * Router-root Suspense fallback. Mounts a portal-backed `app-spinner`
 * marker (C53-SPINNER-ANTI-FLASH-500MS) alongside the existing
 * SuspenseFallbackTimeout so every lazy-loaded route exercises the 500ms
 * anti-flash hold, not just DashboardPage's local mount. The portal lives
 * on document.body so it survives the Suspense unmount when the chunk
 * resolves &lt;500ms after first paint.
 */
function RouterRootSuspenseFallback(): React.JSX.Element {
  return <SuspenseFallbackTimeout />;
}

function CatalogA11ySurface(): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed left-0 top-0 z-0 flex min-h-px min-w-px opacity-[0.01]"
      // Blindagem ESTRUTURAL (incidente 15/07/2026, "shell cru em alguns
      // celulares"): se a folha de estilo falhar (404, parse floor, rede),
      // as classes acima não aplicam e este catálogo virava texto visível
      // ("Ok x / Erro x / webhook-processed..."). Estilo inline sobrevive a
      // qualquer falha de CSS; os elementos continuam renderizados para os
      // specs de catálogo lerem computed styles.
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        maxWidth: 1,
        maxHeight: 1,
        overflow: "hidden",
        opacity: 0.01,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <span
        data-testid="ia-pro-crown"
        className="block h-px w-px animate-pulse"
      />
      <span className="block h-px w-px animate-pulse rounded bg-surface" />
      <div data-testid="splash-loader">
        <div className="h-px w-px animate-spin" />
      </div>
      <span data-testid="balance-refresh">
        <span className="block h-px w-px animate-spin" />
      </span>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="flex items-center gap-3 rounded-xl border border-success bg-card px-4 py-3 text-success shadow-lg backdrop-blur-sm"
      >
        <span className="h-2 w-2 shrink-0 rounded-full bg-success" />
        <span className="text-sm font-medium text-success">Ok</span>
        <button type="button" aria-label="Fechar" tabIndex={-1}>
          x
        </button>
      </div>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="flex items-center gap-3 rounded-xl border border-rose-500/30 bg-card px-4 py-3 text-danger-soft-faint shadow-lg backdrop-blur-sm"
      >
        <span className="h-2 w-2 shrink-0 rounded-full bg-rose-400" />
        <span className="text-sm font-medium text-danger-soft-faint">Erro</span>
        <button type="button" aria-label="Fechar" tabIndex={-1}>
          x
        </button>
      </div>
      <span data-testid="modal-backdrop" className="bg-overlay-scrim" />
      {CATALOG_STATUS_BADGES.map((entry) => (
        <span
          key={entry.id}
          data-status-id={entry.id}
          data-testid={`status-badge-${entry.id}`}
          className={badgeClass(entry.key)}
        >
          {entry.id}
        </span>
      ))}
    </div>
  );
}
